import type { SidebarBadgeProps } from "@tracify/ui/sidebar-badge";
import {
  BUILTIN_DASHBOARD_CORE_ITEMS,
  getBuiltinDashboardDescriptor,
} from "@tracify/shared";
import MarketingOverviewIcon from "@/components/Layout/icons/marketing-overview";
import MetaChannelIcon from "@/components/Layout/icons/meta-channel";
import GoogleChannelIcon from "@/components/Layout/icons/google-channel";
import TikTokChannelIcon from "@/components/Layout/icons/tiktok-channel";
import PinterestChannelIcon from "@/components/Layout/icons/pinterest-channel";
import OutbrainChannelIcon from "@/components/Layout/icons/outbrain-channel";
import MicrosoftChannelIcon from "@/components/Layout/icons/micorosft-channel";
import TaboolaChannelIcon from "@/components/Layout/icons/taboola-channel";
import RealtimeDashboardIcon from "@/components/Layout/icons/realtime-dashboard-icon";
import InfluencerIcon from "@/components/Layout/icons/influencer-icon";
import InfluencerOverviewIcon from "@/components/Layout/icons/influencer-overview-icon";
import InfluencerIconSmall from "@/components/Layout/icons/influencer-icon-small";
import CooperationsIcon from "@/components/Layout/icons/cooperations-icon";
import CreativeDashboardIcon from "@/components/Layout/icons/creative-dashboard";
import CustomerJourneyIcon from "@/components/Layout/icons/customer-journeys";

// Channel configuration for dynamic sidebar generation
export type ChannelConfig = {
  connectedKey: string; // Key used in connectedChannels array
  name: string;
  icon: any;
  basePath: string;
  id: string;
  label?: string;
  badgeVariant?: SidebarBadgeProps["variant"];
};

export const CHANNEL_CONFIG: Record<string, ChannelConfig> = {
  facebook: {
    connectedKey: "facebook",
    name: "Meta Ads",
    icon: MetaChannelIcon,
    basePath: "/channels/meta",
    id: "sidebar-meta-ads",
  },
  google: {
    connectedKey: "google",
    name: "Google Ads",
    icon: GoogleChannelIcon,
    basePath: "/channels/google",
    id: "sidebar-google-ads",
  },
  tiktok: {
    connectedKey: "tiktok",
    name: "TikTok Ads",
    icon: TikTokChannelIcon,
    basePath: "/channels/tiktok",
    id: "sidebar-tiktok-ads",
  },
  pinterest: {
    connectedKey: "pinterest",
    name: "Pinterest Ads",
    icon: PinterestChannelIcon,
    basePath: "/channels/pinterest",
    id: "sidebar-pinterest-ads",
  },
  outbrain: {
    connectedKey: "outbrain",
    name: "Outbrain Ads",
    icon: OutbrainChannelIcon,
    basePath: "/channels/outbrain",
    id: "sidebar-outbrain-ads",
  },
  msads: {
    connectedKey: "msads",
    name: "Microsoft Ads",
    icon: MicrosoftChannelIcon,
    basePath: "/channels/microsoft",
    id: "sidebar-microsoft-ads",
  },
  taboola: {
    connectedKey: "taboola",
    name: "Taboola Ads",
    icon: TaboolaChannelIcon,
    basePath: "/channels/taboola",
    id: "sidebar-taboola-ads",
  },
};

export const ALL_CHANNEL_KEYS = [
  "facebook",
  "google",
  "tiktok",
  "pinterest",
  "outbrain",
  "msads",
  "taboola",
];

export type NavigationType = {
  id: string;
  name: string;
  icon: any;
  href?: string;
  disabled: boolean;
  label?: string;
  badgeVariant?: SidebarBadgeProps["variant"];
  defaultOpen?: boolean;
  matchingHrefs?: string[];
  catchAll?: string;
  children?: NavigationType[];
  basePath?: string; // For channel items to enable click-to-navigate
};

// Base navigation items (before dynamic channels)
export const BASE_NAV_ITEMS: NavigationType[] = [
  {
    name: "Marketing Overview",
    icon: MarketingOverviewIcon,
    href: "/",
    disabled: false,
    label: "",
    id: "sidebar-marketing-overview",
  },
];

// Navigation items that come after channels
export const POST_CHANNEL_NAV_ITEMS: NavigationType[] = [
  {
    name: "Realtime Dashboard",
    icon: RealtimeDashboardIcon,
    href: "/realtime",
    disabled: false,
    id: "sidebar-realtime-dashboard",
  },
  {
    name: "Influencers",
    icon: InfluencerIcon,
    disabled: false,
    defaultOpen: false,
    id: "sidebar-influencers-dropdown",
    children: [
      {
        name: "Overview",
        icon: InfluencerOverviewIcon,
        href: "/influencers/overview",
        disabled: false,
        id: "sidebar-influencer-overview",
      },
      {
        name: "Influencers",
        icon: InfluencerIconSmall,
        href: "/influencers",
        catchAll: "/influencers/import/*",
        disabled: false,
        id: "sidebar-influencers",
      },
      {
        name: "Cooperations",
        icon: CooperationsIcon,
        href: "/influencers/cooperations",
        catchAll: "/influencers/cooperations/*",
        disabled: false,
        id: "sidebar-cooperations",
      },
    ],
  },
  {
    name: "Creative Dashboard",
    icon: CreativeDashboardIcon,
    disabled: false,
    defaultOpen: false,
    id: "sidebar-creative-dashboard",
    children: [
      {
        name: "Comparison",
        icon: CreativeDashboardIcon,
        href: "/creatives/comparison",
        disabled: false,
        id: "sidebar-creative-comparison",
      },
      {
        name: "Individual Analysis",
        icon: CreativeDashboardIcon,
        href: "/creatives/analysis",
        disabled: false,
        id: "sidebar-creative-analysis",
      },
    ],
  },
  {
    name: "Customer Journeys",
    icon: CustomerJourneyIcon,
    href: "/customer-journeys",
    disabled: false,
    id: "sidebar-customer-journeys",
  },
];

type MentionDashboardItem = {
  id: string;
  label: string;
};

export function getDashboardMentionItems(
  connectedChannels: string[]
): MentionDashboardItem[] {
  const items: MentionDashboardItem[] = [];

  for (const key of ALL_CHANNEL_KEYS) {
    if (!connectedChannels.includes(key)) continue;
    const config = CHANNEL_CONFIG[key];
    if (!config) continue;
    const dashboardPath = `${config.basePath}/dashboard`;
    const builtinDashboard = getBuiltinDashboardDescriptor(dashboardPath);
    items.push({
      id: dashboardPath,
      label: builtinDashboard?.label ?? `${config.name} Dashboard`,
    });
  }

  const seen = new Set<string>();
  const merged = [...BUILTIN_DASHBOARD_CORE_ITEMS, ...items].filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });

  return merged;
}
